import { useCallback, useEffect, useRef, useState } from 'react';

export type MutationOutcome<T> =
  | { status: 'success'; data: T }
  | { status: 'error'; error: Error }
  | { status: 'aborted' };

export interface UseMutationReturn<TData, TVariables> {
  data: TData | null;
  loading: boolean;
  error: Error | null;
  mutate: (variables: TVariables) => Promise<MutationOutcome<TData>>;
  reset: () => void;
}

function useMutation<TData, TVariables>(
  mutationFn: (
    variables: TVariables,
    abortController: AbortController
  ) => Promise<TData>
): UseMutationReturn<TData, TVariables> {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const mutationFnRef = useRef(mutationFn);
  mutationFnRef.current = mutationFn;

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  const mutate = useCallback(async (variables: TVariables) => {
    const current = abortRef.current;
    if (current && !current.signal.aborted)
      return { status: 'aborted' as const };
    const abortController = new AbortController();
    abortRef.current = abortController;
    setLoading(true);
    setError(null);

    try {
      const result = await mutationFnRef.current(variables, abortController);
      if (abortController.signal.aborted) return { status: 'aborted' as const };
      setData(result);
      return { status: 'success' as const, data: result };
    } catch (err) {
      if (abortController.signal.aborted) return { status: 'aborted' as const };
      const failure = err instanceof Error ? err : new Error(String(err));
      setError(failure);
      return { status: 'error' as const, error: failure };
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
        if (abortRef.current === abortController) abortRef.current = null;
      }
    }
  }, []);

  return { data, loading, error, mutate, reset };
}

export default useMutation;
